(function () {
  const CONTROLLER_KEY = '_faqAccordionController';
  const ANIMATION_DURATION = 200;

  class FaqAccordionController {
    constructor(root) {
      this.root = root;
      this.oneAtATime = root.getAttribute('data-one-at-a-time') !== 'false';
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.items = Array.from(root.querySelectorAll('details[data-faq-details]'))
        .map((details) => ({
          details,
          summary: details.querySelector('.faq__summary'),
          content: details.querySelector('.faq__answer'),
        }))
        .filter((item) => item.summary && item.content);

      if (!this.items.length) return;

      this.enforceSingleOpenOnLoad();
      this.bindEvents();
      this.syncStates();
    }

    enforceSingleOpenOnLoad() {
      if (!this.oneAtATime) return;

      let foundOpen = false;

      this.items.forEach(({ details }) => {
        if (!details.open) return;

        if (foundOpen) {
          details.open = false;
        } else {
          foundOpen = true;
        }
      });
    }

    syncStates() {
      this.items.forEach(({ details, summary }) => {
        summary.setAttribute('role', 'button');
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        details.dataset.state = details.open ? 'open' : 'closed';
      });
    }

    bindEvents() {
      this.items.forEach((item) => {
        item.summary.addEventListener('click', (event) => this.onSummaryClick(event, item), { signal: this.signal });
        item.summary.addEventListener('keydown', (event) => this.onSummaryKeydown(event, item), { signal: this.signal });
      });
    }

    onSummaryKeydown(event, item) {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      event.preventDefault();
      this.toggle(item);
    }

    onSummaryClick(event, item) {
      event.preventDefault();
      this.toggle(item);
    }

    toggle(item) {
      if (item.details.open) {
        this.close(item);
        return;
      }

      if (this.oneAtATime) {
        this.items.forEach((other) => {
          if (other !== item && other.details.open) {
            this.close(other, false);
          }
        });
      }

      this.open(item);
    }

    open(item) {
      const { details, summary, content } = item;

      details.open = true;
      details.dataset.state = 'open';
      summary.setAttribute('aria-expanded', 'true');

      if (this.prefersReducedMotion) return;

      content.style.overflow = 'hidden';
      content.style.height = '0px';

      requestAnimationFrame(() => {
        content.style.transition = `height ${ANIMATION_DURATION}ms ease-out`;
        content.style.height = `${content.scrollHeight}px`;
      });

      content.addEventListener(
        'transitionend',
        (event) => {
          if (event.propertyName !== 'height') return;

          content.style.height = '';
          content.style.overflow = '';
          content.style.transition = '';
        },
        { once: true }
      );
    }

    close(item, animate = true) {
      const { details, summary, content } = item;

      if (!details.open) return;

      if (!animate || this.prefersReducedMotion) {
        details.open = false;
        details.dataset.state = 'closed';
        summary.setAttribute('aria-expanded', 'false');
        content.style.height = '';
        content.style.overflow = '';
        content.style.transition = '';
        return;
      }

      content.style.overflow = 'hidden';
      content.style.height = `${content.scrollHeight}px`;

      requestAnimationFrame(() => {
        content.style.transition = `height ${ANIMATION_DURATION}ms ease-out`;
        content.style.height = '0px';
      });

      content.addEventListener(
        'transitionend',
        (event) => {
          if (event.propertyName !== 'height') return;

          details.open = false;
          details.dataset.state = 'closed';
          summary.setAttribute('aria-expanded', 'false');
          content.style.height = '';
          content.style.overflow = '';
          content.style.transition = '';
        },
        { once: true }
      );
    }

    destroy() {
      this.abortController.abort();
    }
  }

  function initFaqAccordions(scope) {
    const roots = (scope || document).querySelectorAll('[data-faq-accordion]');

    roots.forEach((root) => {
      if (root[CONTROLLER_KEY]) {
        root[CONTROLLER_KEY].destroy();
        root[CONTROLLER_KEY] = null;
      }

      root[CONTROLLER_KEY] = new FaqAccordionController(root);
    });
  }

  window.initFaqAccordions = initFaqAccordions;

  document.addEventListener('DOMContentLoaded', () => initFaqAccordions(document));
  document.addEventListener('shopify:section:load', (event) => initFaqAccordions(event.target));
  document.addEventListener('shopify:section:reorder', () => initFaqAccordions(document));

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initFaqAccordions(document);
  }
})();
